/**
 * WARNING:
 * Do not import anything into this file from other domains.
 * Otherwise you will risk a circular dependency.
 * */
import { ContestStatus, ContestType, Sport } from '@betterpool/api-types/contests-service';
import { SlateStatus } from '@betterpool/api-types/games-service';

import type {
  AsideContentType,
  ContentURLType,
} from '~/domains/contest/domains/game/domains/common/types/aside';
import type { ContestStatusFilter } from '~/domains/contest/domains/services/enums/contest-status-filter.enum';
import type { ContestTab } from '~/domains/contest/domains/entries/hooks/useEntriesTabsProps';
import type { ActiveTab as CommishActiveTab } from '~/domains/commissioner/enums';

import { URLQueryKeys } from '../types/URLQueryKeys';
import { createQueryString } from '../utils/queryString';

const routeWithQuery =
  <P extends Record<string, string | number | boolean | string[]>>(route: string) =>
  (queryParams?: P) => {
    const queryString = queryParams ? createQueryString(queryParams) : '';
    return `${route}${queryString}`;
  };

const EO_SUBDOMAIN = process.env.NEXT_PUBLIC_EO_SUBDOMAIN ?? '';
const CONTEST_V2_SUBDOMAIN = process.env.NEXT_PUBLIC_CONTEST_V2_SUBDOMAIN ?? '';

const ensureTrailingSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);
const stripTrailingSlash = (url: string) => url.replace(/\/+$/, '');

const getRypBaseUrl = () => {
  const fromEnv = process.env.NEXT_PUBLIC_RYP_BASE_URL;
  if (fromEnv) return ensureTrailingSlash(fromEnv);
  return `https://${EO_SUBDOMAIN}.runyourpool.com/`;
};

const getOfpBaseUrl = () => {
  const fromEnv = process.env.NEXT_PUBLIC_OFP_BASE_URL;
  if (fromEnv) return ensureTrailingSlash(fromEnv);
  return `https://${EO_SUBDOMAIN}.officefootballpool.com/`;
};

// Callers append a leading-slash path (`/contest/...`), so the base must not
// carry a trailing slash — otherwise the env-provided URL yields a double slash.
const getContestV2Url = () => {
  const fromEnv = process.env.NEXT_PUBLIC_CONTEST_V2_URL;
  if (fromEnv) return stripTrailingSlash(fromEnv);
  return `https://${CONTEST_V2_SUBDOMAIN}.splashsports.com`;
};

type ActiveTab = {
  [URLQueryKeys.ACTIVE_TAB]?: string;
};

type CommishActiveTabQuery = {
  [URLQueryKeys.ACTIVE_TAB]?: CommishActiveTab;
  [URLQueryKeys.RESTART_STATUS]?: boolean;
};

type ContestStatusQuery = {
  [URLQueryKeys.CONTEST_STATUS]?: ContestStatus;
};

type Redirect = {
  [URLQueryKeys.REDIRECT_TO]?: string;
  [URLQueryKeys.REDIRECT_TO_EO]?: string;
  [URLQueryKeys.CONTEST_ID]?: string;
  [URLQueryKeys.REF_PLATFORM]?: string;
  [URLQueryKeys.REF]?: string;
  [URLQueryKeys.REFERRAL_CODE]?: string;
  [URLQueryKeys.BRAND]?: string;
  [URLQueryKeys.PLATFORM]?: string;
  [URLQueryKeys.RESET_PASSWORD_SUCCESS]?: string;
  [URLQueryKeys.REQUIRE_KYC]?: string;
};

type ContestDetailOptions = {
  [URLQueryKeys.REF]?: string;
  [URLQueryKeys.UTM_CAMPAIGN]?: string;
  [URLQueryKeys.UTM_CONTENT]?: string;
  [URLQueryKeys.UTM_SOURCE]?: string;
  [URLQueryKeys.UTM_MEDIUM]?: string;
  [URLQueryKeys.ENTER_CONTEST]?: boolean;
};

type SearchQuery = {
  [URLQueryKeys.SEARCH_QUERY]?: string;
};

type EntrantsManagerQuery = {
  [URLQueryKeys.CONTEST_USER]?: string;
};

export const Routes = {
  marketplaceOffer: (offerId: string) => `/marketplace/holdings?tab=offers&offer=${offerId}`,
  gamecenterMarketplace: (contestId: string) => `/contest/${contestId}/marketplace`,
  gamecenterMarketplaceOffers: (contestId: string) => `/contest/${contestId}/marketplace/offers`,
  groupInvite: (inviteId: string) => `/group/invites/${inviteId}`,
  groupEntryCreate: (contestId: string, entryId: string) =>
    `/contest/${contestId}/entry/${entryId}/group/create`,
  search: (q?: string) => routeWithQuery<SearchQuery>('/search')({ q }),
  notifications: () => '/notifications',
  commissioner: routeWithQuery<CommishActiveTabQuery>('/commissioner'),
  channel: (handle: string, q?: string, contestStatus?: ContestStatus) =>
    routeWithQuery<SearchQuery | ContestStatusQuery>(`/channel/${handle}`)({ q, contestStatus }),
  channelEdit: (handle: string) => `/channel/${handle}/edit`,
  entrantsManager: (contestUuid: string, queryParams?: EntrantsManagerQuery) =>
    routeWithQuery<EntrantsManagerQuery>(`/entries-manager/${contestUuid}`)(queryParams),
  manageContest: (contestUuid: string) => `/manage-contest/${contestUuid}`,
  serviceFee: (contestUuid: string) => `/manage-contest/${contestUuid}/service-fee`,
  /** @deprecated Channel invite functionality removed. Route now redirects to user's own channel page. */
  invite: (handle: string, q?: string) =>
    routeWithQuery<SearchQuery>(`/channel/${handle}/invite`)({ q }),
  channelSearch: (
    handle: string,
    activeTab?: URLQueryKeys.CHANNEL_FOLLOWERS | URLQueryKeys.CHANNEL_FOLLOWING,
    q?: string
  ) => routeWithQuery<ActiveTab & SearchQuery>(`/channel/${handle}/search`)({ q, activeTab }),
  invitesManager: (contestUuid: string, activeTab = '') =>
    `/invites-manager/${contestUuid}?activeTab=${activeTab}`,
  invitesManagerNew: (contestUuid: string, activeTab = '') =>
    `/invites-manager/${contestUuid}/new?activeTab=${activeTab}`,
  invitesManagerReview: (contestUuid: string, activeTab = '') =>
    `/invites-manager/${contestUuid}/review?activeTab=${activeTab}`,

  createAContest: routeWithQuery<Redirect>('/contest/create'),
  restartAContest: (contestId: string) =>
    routeWithQuery<Redirect>(`/contest/${contestId}/restart`)(),
  editAContest: (contestId: string, queryParams?: Redirect) => {
    const baseRoute = `/contest/${contestId}/edit`;
    const queryString = queryParams ? createQueryString(queryParams) : '';
    return `${baseRoute}${queryString}`;
  },

  gameshell: ({
    contestId,
    sport,
    contestType,
    isSalary,
  }: {
    contestId: string;
    sport: Sport;
    contestType: ContestType;
    isSalary?: boolean;
  }) => {
    const parsedSport = sport?.replace(/_/g, '-');
    const parsedContestType = contestType?.replace(/_/g, '-');

    return `/contest/${contestId}/game/${parsedSport}/${parsedContestType}${
      isSalary ? '/salary' : ''
    }`;
  },
  gameshellPicksheet: ({
    contestId,
    sport,
    contestType,
    entryId,
    referrer,
    slateId,
    editingPicks,
    isSalary,
    isNewGameshell,
    isNewEntry,
    aside,
  }: {
    contestId: string;
    sport: Sport;
    contestType: ContestType;
    entryId?: string;
    referrer?: string;
    slateId?: string;
    editingPicks?: boolean;
    isSalary?: boolean;
    isNewGameshell?: boolean;
    isNewEntry?: boolean;
    aside?: AsideContentType;
  }) => {
    const oldGameshellPath = routeWithQuery<{
      [URLQueryKeys.ENTRY_ID]?: string;
      [URLQueryKeys.SLATE_ID]?: string;
      [URLQueryKeys.REF]?: string;
      [URLQueryKeys.EDITING]?: string;
      [ContentURLType.ASIDE]?: AsideContentType;
    }>(`/contest/${contestId}/game`)({
      entryId,
      slateId,
      ref: referrer,
      aside,
      editingPicks: editingPicks ? 'true' : undefined,
    });
    const newGameshellBasePath = Routes.gameshell({
      contestId,
      sport,
      contestType,
      isSalary,
    });
    const newGameshellPath = routeWithQuery<{
      [URLQueryKeys.SLATE_ID]?: string;
      [URLQueryKeys.ENTRY_ID]?: string;
      [URLQueryKeys.REF]?: string;
      [URLQueryKeys.IS_NEW_ENTRY]?: boolean;
      [URLQueryKeys.UTM_PARAM_CONTEST_ID]?: string;
      [URLQueryKeys.UTM_CAMPAIGN]?: ContestType;
      [URLQueryKeys.UTM_MEDIUM]?: string;
      [ContentURLType.ASIDE]?: AsideContentType;
    }>(`${newGameshellBasePath}/pick`)({
      slateId,
      entryId,
      ref: referrer,
      utm_param_contest_id: contestId,
      utm_medium: 'picksheet',
      utm_campaign: contestType,
      aside,
      ...(isNewEntry && { isNewEntry }),
    });

    switch (sport) {
      case Sport.FOOTBALL:
      case Sport.BASKETBALL: {
        // TODO: Once we get rid off old gameshell we can remove this condition
        // for SportTag.BASKETBALL and SportTag.FOOTBALL
        if (
          contestType === ContestType.PLAYER_TIER &&
          sport === Sport.FOOTBALL &&
          !isNewGameshell
        ) {
          return oldGameshellPath;
        }

        return newGameshellPath;
      }
      case Sport.GOLF: {
        // TODO: Once we get rid off old gameshell we can remove this condition for SportTag.GOLF
        if (contestType === ContestType.PLAYER_ONE_AND_DONE) {
          return newGameshellPath;
        }

        return isNewGameshell ? newGameshellPath : oldGameshellPath;
      }
      default:
        return isNewGameshell ? newGameshellPath : oldGameshellPath;
    }
  },
  contestDetail: (contestId: string, options?: ContestDetailOptions) =>
    routeWithQuery<ContestDetailOptions>(`/contest/${contestId}/detail`)(options),
  gamecenterPlayers: (contestId: string) => `/contest/${contestId}/players`,
  brazeGamecenter: (contestId: string) => routeWithQuery(`/contest/${contestId}/gamecenter`)(),
  brazeGamecenterEntriesOverall: (contestId: string) =>
    routeWithQuery(`/contest/${contestId}/entries/overall`)(),
  brazeQuickPicksStandings: (slipId: string) =>
    routeWithQuery(`/quick-picks/slip/${slipId}/standings`)(),
  contestPosts: (contestId: string) => `/contest/${contestId}/posts`,
  signIn: routeWithQuery<Redirect>('/sign-in'),
  signUp: routeWithQuery<Redirect>('/sign-up'),
  home: () => '/home',
  main: () => '/',
  rypBaseUrl: getRypBaseUrl,
  ofpBaseUrl: getOfpBaseUrl,
  createAContestExternalUrl: () => `${getContestV2Url()}/contest/create`,
  contestV2ResolvedUrl: (
    path: string,
    query?: Record<string, string | string[] | boolean | number | undefined>
  ) =>
    `${getContestV2Url()}${path.startsWith('/') ? path : `/${path}`}${
      query ? createQueryString(query) : ''
    }`,
  authRYPMobile: () => {
    const env = process.env.NEXT_PUBLIC_ENV;
    const envPrefixes: Record<string, string> = {
      staging: '-s',
      development: '-d',
      localhost: '-d',
    };

    return `combetterpoolrunyourpool${envPrefixes[env] || ''}://runyourpool/authenticated`;
  },
  logout: () => '/logout',
  myEntries: routeWithQuery<{
    [URLQueryKeys.CONTEST_TYPE]?: ContestType;
    [URLQueryKeys.ENTRY_STATUS]?: ContestStatusFilter;
  }>('/my-entries'),
  entries: routeWithQuery<{
    [URLQueryKeys.CONTEST_TAB]?: ContestTab;
    [URLQueryKeys.CONTEST_TYPE]?: ContestType;
    [URLQueryKeys.CONTEST_STATUS]?: ContestStatusFilter;
  }>('/entries'),
  contestLobby: routeWithQuery<{
    [URLQueryKeys.CONTEST_TYPE]?: ContestType;
    [URLQueryKeys.LEAGUE]?: string;
    [URLQueryKeys.LEAGUE_NAME]?: string;
  }>('/contest-lobby'),
  account: routeWithQuery<{ open?: 'externalAccounts' | 'transactionHistory' } & Redirect>(
    `/account`
  ),
  forgotPassword: routeWithQuery<Redirect>('/account/forgot-password'),
  resetPassword: routeWithQuery<Redirect>('/account/reset-password'),
  deposit: routeWithQuery<{ [URLQueryKeys.DEPOSIT_AMOUNT]?: number } & Redirect>(
    '/account/wallet/deposit'
  ),
  withdraw: routeWithQuery<{ [URLQueryKeys.WITHDRAWAL_AMOUNT]?: number } & Redirect>(
    '/account/wallet/withdraw'
  ),
  withdrawTaxInformation: () => '/account/wallet/withdraw/tax-information',
  withdrawRequirements: () => '/account/wallet/withdraw/requirements',
  withdrawVerifyIdentity: () => '/account/wallet/withdraw/verify-identity',
  userVerification: routeWithQuery<Redirect>('/user-verification'),
  userVerificationAccept: routeWithQuery<Redirect>('/user-verification/accept'),
  appSplashContestDetailRedirect: (id: string) =>
    `${process.env.NEXT_PUBLIC_NATIVE_APP_VIPP}://contest?id=${id}`,
  legal: () => 'https://legal.splashsports.com/',
  termsOfUse: (isCanada) =>
    isCanada
      ? 'https://legal.splashsports.com/legal/splash-sports-terms-of-use-canada'
      : 'https://legal.splashsports.com/legal/terms-of-use',
  entryMarketplaceTerms: () => 'https://legal.splashsports.com/legal/entry-marketplace-terms',
  privacyPolicy: (isCanada) =>
    isCanada
      ? 'https://legal.splashsports.com/legal/splash-sports-privacy-policy-canada'
      : 'https://legal.splashsports.com/legal/privacy-policy',
  quickPickRules: 'https://legal.splashsports.com/legal/splash-sports-quickpicks-rules',
  entryZipTerms: 'https://legal.splashsports.com/promotion-terms/pga-champ-terms',
  responsibleGaming: () => 'https://legal.splashsports.com/legal/responsible-gaming',
  helpCenter: () => 'https://intercom.help/splashsports-helpcenter/en/',
  helpCenterCommissioner: () =>
    'https://intercom.help/splashsports-helpcenter/en/collections/12825176-commissioner-help',
  groupEntriesHowItWorks: () => 'https://splashsports.com/blog/group-entries',
  gamecenter: ({
    contestId,
    contestStatus,
    userEntriesCount,
    promoSource,
    promoIndex,
    contestType,
    currentSlateStatus,
  }: {
    contestId: string;
    contestStatus: ContestStatus;
    userEntriesCount: number;
    promoSource?: string;
    promoIndex?: number;
    contestType?: ContestType;
    currentSlateStatus?: SlateStatus;
  }) => {
    if (userEntriesCount > 0) {
      if (contestStatus === ContestStatus.SCHEDULED) {
        return Routes.gamecenterEntriesOverall(contestId);
      }
      if (
        contestType === ContestType.TEAM_PICK_EM &&
        [SlateStatus.LIVE, SlateStatus.IN_PROGRESS].includes(currentSlateStatus)
      ) {
        return Routes.gamecenterStandingsSlates(contestId);
      }

      return Routes.gamecenterStandingsOverall(contestId);
    }

    return Routes.contestDetail(contestId, {
      [URLQueryKeys.UTM_SOURCE]: promoSource,
      [URLQueryKeys.UTM_CAMPAIGN]: `${promoIndex}`,
    });
  },
  gamecenterStandingsOverall: (
    contestId: string,
    {
      entryId,
      entrySlateId,
      entryUsername,
    }: { entryId?: string; entrySlateId?: string; entryUsername?: string } = {}
  ) =>
    `/contest/${contestId}/standings/overall${createQueryString({
      entryId,
      entrySlateId,
      entryUsername,
    })}`,
  gamecenterStandingsSlates: (
    contestId: string,
    {
      entryId,
      slateId,
      entryUsername,
      entrySlateId,
    }: { entryId?: string; slateId?: string; entryUsername?: string; entrySlateId?: string } = {}
  ) =>
    `/contest/${contestId}/standings/slates${createQueryString({
      entryId,
      slateId,
      entryUsername,
      entrySlateId,
    })}`,
  gamecenterEntriesOverall: (contestId: string) => `/contest/${contestId}/entries/overall`,
  gamecenterEntrants: (contestId: string) => `/contest/${contestId}/entrants`,
  gamecenterEntriesSlates: (
    contestId: string,
    { entryId, slateId }: { entryId?: string; slateId?: string } = {}
  ) => `/contest/${contestId}/entries/slates${createQueryString({ entryId, slateId })}`,
  gamecenterPicks: (
    contestId: string,
    {
      entryId,
      slateId,
      tierId,
      tierPickIndex,
      [URLQueryKeys.IS_COMMISSIONER]: isCommissioner,
      [URLQueryKeys.CONTEST_USER]: contestUser,
    }: {
      entryId?: string;
      slateId?: string;
      tierId?: number;
      tierPickIndex?: number;
      [URLQueryKeys.IS_COMMISSIONER]?: boolean;
      [URLQueryKeys.CONTEST_USER]?: string;
    } = {}
  ) =>
    `/contest/${contestId}/picks${createQueryString({
      entryId,
      slateId,
      tierId,
      tierPickIndex,
      [URLQueryKeys.IS_COMMISSIONER]: isCommissioner,
      [URLQueryKeys.CONTEST_USER]: contestUser,
    })}`,
  gamecenterStats: (contestId: string) => `/contest/${contestId}/stats/slate-summary`,
  gamecenterStatsAvailability: (contestId: string) => `/contest/${contestId}/stats/availability`,
  gamecenterAvailabilityAvailable: (contestId: string) =>
    `/contest/${contestId}/availability/available`,
  gamecenterAvailabilityUnavailable: (contestId: string) =>
    `/contest/${contestId}/availability/unavailable`,
  appDownloadPrompt: routeWithQuery<Redirect>('/app-download'),
  sutError: () => '/sut/error',
  quickPicksBoard: routeWithQuery<{
    [URLQueryKeys.PLAYER_CARD_ID]?: string;
    [URLQueryKeys.LEAGUE]?: string;
    [URLQueryKeys.GAME]?: string[];
    [URLQueryKeys.TYPE]?: string;
    [URLQueryKeys.PICKSDRAWER]?: boolean;
    [URLQueryKeys.PROPS]?: string;
  }>('/quick-picks/board'),
  quickPicksBoardSearch: routeWithQuery<{
    [URLQueryKeys.LEAGUE]?: string;
    [URLQueryKeys.GAME]?: string[];
    [URLQueryKeys.TYPE]?: string;
  }>('/quick-picks/board/search'),
  quickPicksBoardLeague: (league: string) => `/quick-picks/board/${league}`,
  quickPicksPopular: routeWithQuery<{
    [URLQueryKeys.PLAYER_CARD_ID]?: string;
    [URLQueryKeys.LEAGUE]?: string;
    [URLQueryKeys.GAME]?: string[];
    [URLQueryKeys.TYPE]?: string;
    [URLQueryKeys.PICKSDRAWER]?: boolean;
    [URLQueryKeys.PROPS]?: string;
  }>('/quick-picks/popular'),
  quickPicksActive: () => '/quick-picks/active',
  quickPicksFinal: () => '/quick-picks/final',
  quickPicksSlipStandings: (slipId: string) => `/quick-picks/slip/${slipId}/standings`,
  quickPicksSlipMyPicks: (slipId: string) => `/quick-picks/slip/${slipId}/my-picks`,
  quickPicksSlipDetail: (slipId: string) => `/quick-picks/slip/${slipId}/detail`,
  quickPicksSplitSlip: (slipId: string) => `/quick-picks/slip/${slipId}/split`,
  // Entry marketplace (distinct from the `markets` prediction-markets product below).
  //
  // INVARIANT — these marketplace URLs are a public API. The marketplace renders inside the native
  // iOS/Android webview, and the apps deep-link these URLs directly, so every path and query param
  // below is part of the mobile contract: renaming or dropping one breaks installed clients.
  // Refactor freely, but these shapes must keep resolving — and resolve semantically, not just
  // return 200 (a 200 that renders the wrong surface is the failure this guards).
  marketplace: () => '/marketplace',
  marketplaceContest: (
    contestId: string,
    // ?tab picks the Buy-now / All-entries tab; omitted means the page's default — Buy-now when the
    // contest has listings, All-entries when it has none — so deep links that need a specific tab
    // must state it. ?view toggles the active tab's team-picks grid (default owners, omit it) and
    // pins ?tab when on; ?listing opens a listing panel; ?sale opens a Sold-tab sale panel.
    query?: {
      tab?: 'buy_now' | 'all_entries' | 'sold';
      view?: 'teams';
      listing?: string;
      sale?: string;
    }
  ) =>
    routeWithQuery<{ tab?: string; view?: string; listing?: string; sale?: string }>(
      `/marketplace/contest/${contestId}`
    )(query),
  marketplaceListing: (listingId: string) => `/marketplace/listing/${listingId}`,
  marketplaceOffers: routeWithQuery<{
    // A row link must carry the Buy | Sell column so opening a panel
    // (?entry / ?buyerListing / ?buyerOffer) never drops back to the Buy default.
    offers?: 'buy' | 'sell';
    offer?: string;
    entry?: string;
    buyerListing?: string;
    buyerOffer?: string;
    // ?buyerOfferId names the specific offer so its state (incl. closed) drives the panel.
    buyerOfferId?: string;
    // The clicked Sell row's offer id, so a closed sell offer renders in the Selling panel.
    sellerOfferId?: string;
  }>('/marketplace/offers'),
  marketplaceHoldings: routeWithQuery<{
    tab?: 'holdings' | 'listings';
    // ?entry opens the entry panel (holdings and listings both select an entry); ?buyerListing opens
    // the public ListingDetail for a buyer viewing a listing they bid on.
    entry?: string;
    buyerListing?: string;
    // This builder declares only the holdings panels; useMarketplacePanelRoute additionally resolves
    // buyerOffer / buyerOfferId / sellerOfferId from the URL. A future holdings deep-link to a buyer
    // offer needs those added here — the resolver already reads them, so it is not broken today.
  }>('/marketplace/holdings'),
  // Marketplace Partners (web-only): the trading-partner draft-offers surface. A bare path with no
  // query params — nothing here is a mobile webview param.
  marketplacePartners: () => '/marketplace/partners',
  marketplaceSettings: routeWithQuery<{ contestId?: string; view?: 'add' }>(
    '/marketplace/settings'
  ),
  markets: () => '/markets',
  marketsMyPositions: () => '/markets/my-positions',
  marketsSport: (sport: string, type = 'gameline') => `/markets/${sport}/${type}`,
  marketsSportMarketGroup: (sport: string, type: string, marketGroupId: string) =>
    `/markets/${sport}/${type}/${marketGroupId}`,
  marketsFutureMarketGroup: (sport: string, marketGroupId: string) =>
    `/markets/${sport}/future_team_champion/${marketGroupId}`,
  marketsEvent: (marketId: string) => `/markets/event/${marketId}`,
  marketsEventGameLines: (marketId: string) => `/markets/event/${marketId}/markets`,
  marketsEventMatchup: (marketId: string) => `/markets/event/${marketId}/matchup`,
  marketsEventPlayerProps: (marketId: string) => `/markets/event/${marketId}/player-props`,
  marketsEventPlayerPropsSection: (marketId: string, propType: string, teamId?: string) =>
    `/markets/event/${marketId}/player-props/${propType}${teamId ? `?team=${teamId}` : ''}`,
  marketsVerification: () => '/markets/verification',
  marketsVerificationIdentity: () => '/markets/verification/identity',
  marketsVerificationFinancialProfile: () => '/markets/verification/financial-profile',
  marketsVerificationTradingExperience: () => '/markets/verification/trading-experience',
  marketsVerificationAcknowledgement: () => '/markets/verification/acknowledgement',
  marketsVerificationDocuments: () => '/markets/verification/documents',
  marketsVerificationDocumentsVerifying: () => '/markets/verification/documents-verifying',
  marketsVerificationReview: () => '/markets/verification/review',
  marketsVerificationSuccess: () => '/markets/verification/success',
  marketsVerificationError: () => '/markets/verification/error',
  promotions: () => '/promotions',
  redirectToEoRyp: (path: string) =>
    `${getRypBaseUrl().replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`,
  redirectToEoOfp: (path: string) =>
    `${getOfpBaseUrl().replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`,
  restartRYP: (contestId: string) =>
    `${getRypBaseUrl()}contest/?uid=${contestId}&destination=restart`,
  restartOFP: (contestId: string) =>
    `${getOfpBaseUrl()}contest/?uid=${contestId}&destination=restart`,
  pricingContent: (leagueAlias: string, contestType: string, brand: string) =>
    `${getRypBaseUrl()}shared_includes_v5/pricing-content.cfm?leagueAlias=${leagueAlias}&contestType=${contestType}&brand=${brand}`,
  paymentServiceFee: (contestId: string) =>
    `https://${process.env.NEXT_PUBLIC_EO_PAYMENT_SUBDOMAIN}.splashsports.com/shared_includes_v5/payment/fee_overview.cfm?contestId=${contestId}`,
  serviceFeeReceipt: (receiptUrl: string) =>
    `https://${process.env.NEXT_PUBLIC_EO_PAYMENT_SUBDOMAIN}.splashsports.com${receiptUrl}`,
  proveSuccess: () => '/prove/success',
  averageResults: () => '/average-results',
};
